import React from "react";
import { formatAttractionDuration } from "../../utils/attractions";
import "./PackageOptionalAttractionCard.scss";

function Stepper({ label, value, onChange, disabled, min = 0, max = 99 }) {
  const v = Math.max(min, Math.min(max, Math.floor(Number(value) || 0)));
  const dec = () => {
    if (disabled) return;
    onChange(Math.max(min, v - 1));
  };
  const inc = () => {
    if (disabled) return;
    onChange(Math.min(max, v + 1));
  };

  return (
    <div className="pkg-opt-attr-card__stepper" role="group" aria-label={label}>
      <span className="pkg-opt-attr-card__stepper-label">{label}</span>
      <div className="pkg-opt-attr-card__stepper-controls">
        <button
          type="button"
          className="pkg-opt-attr-card__step-btn"
          onClick={dec}
          disabled={disabled || v <= min}
          aria-label={`הפחת ${label}`}
        >
          −
        </button>
        <span className="pkg-opt-attr-card__step-value" aria-live="polite">
          {v}
        </span>
        <button
          type="button"
          className="pkg-opt-attr-card__step-btn"
          onClick={inc}
          disabled={disabled || v >= max}
          aria-label={`הוסף ${label}`}
        >
          +
        </button>
      </div>
    </div>
  );
}

/**
 * @param {{
 *   item: object,
 *   adultPrice: number|null,
 *   childPrice: number|null,
 *   currency: string,
 *   selectedAdults: number,
 *   selectedChildren: number,
 *   subtotalIls: number,
 *   lineLocked?: boolean,
 *   childEligible?: boolean,
 *   onQuantityChange: (next: { adults: number, children: number }) => void,
 *   onOpenDetails?: () => void,
 *   className?: string,
 *   showQuantityControls?: boolean,
 *   showPriceRow?: boolean,
 * }} props
 */
export default function PackageOptionalAttractionCard({
  item,
  adultPrice,
  childPrice,
  currency,
  selectedAdults,
  selectedChildren,
  subtotalIls,
  lineLocked = false,
  childEligible = true,
  onQuantityChange,
  onOpenDetails,
  className = "",
  showQuantityControls = true,
  showPriceRow = true,
}) {
  if (!item || typeof item !== "object") return null;

  const raw = item.raw && typeof item.raw === "object" ? item.raw : {};
  const cat = raw.catalog && typeof raw.catalog === "object" ? raw.catalog : {};
  const thumbUrl =
    cat.image_url ||
    raw.image_url ||
    (Array.isArray(cat.gallery_urls) && cat.gallery_urls[0]
      ? typeof cat.gallery_urls[0] === "string"
        ? cat.gallery_urls[0]
        : cat.gallery_urls[0]?.url
      : null);

  const dur = formatAttractionDuration(item.durationMinutes);
  const fmt = (n) =>
    n != null && Number.isFinite(Number(n))
      ? `${Math.round(Number(n)).toLocaleString()} ${currency === "ILS" ? "₪" : currency}`
      : "—";

  const disabled = Boolean(lineLocked);

  const rootClass = [
    "pkg-opt-attr-card",
    !showQuantityControls ? "pkg-opt-attr-card--no-controls" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <article className={rootClass.trim()}>
      <div className="pkg-opt-attr-card__main">
        <div className="pkg-opt-attr-card__top-row">
          {thumbUrl ? (
            <div className="pkg-opt-attr-card__thumb-wrap">
              <img src={thumbUrl} alt="" className="pkg-opt-attr-card__thumb" loading="lazy" />
            </div>
          ) : (
            <div className="pkg-opt-attr-card__thumb-wrap pkg-opt-attr-card__thumb-wrap--placeholder" aria-hidden>
              <span className="pkg-opt-attr-card__thumb-ph">🎯</span>
            </div>
          )}
          <div className="pkg-opt-attr-card__title-block">
        <h4 className="pkg-opt-attr-card__name">{item.name || "—"}</h4>
        {item.category ? (
          <span className="pkg-opt-attr-card__category">{item.category}</span>
        ) : null}
        {item.descriptionShort ? (
          <p className="pkg-opt-attr-card__desc">{item.descriptionShort}</p>
        ) : null}
        {showPriceRow ? (
        <div className="pkg-opt-attr-card__prices">
          <span>מבוגר: {fmt(adultPrice)}</span>
          <span>
            ילד:{" "}
            {childEligible ? fmt(childPrice) : "לא זמין לילדים"}
          </span>
        </div>
        ) : null}
        {dur ? <div className="pkg-opt-attr-card__dur">{dur}</div> : null}
        {typeof onOpenDetails === "function" ? (
          <button
            type="button"
            className="pkg-opt-attr-card__details-btn"
            onClick={onOpenDetails}
          >
            פרטים נוספים
          </button>
        ) : null}
          </div>
        </div>
      </div>
      {showQuantityControls ? (
      <div className="pkg-opt-attr-card__controls">
        <Stepper
          label="מבוגרים"
          value={selectedAdults}
          disabled={disabled}
          onChange={(adults) =>
            onQuantityChange({ adults, children: selectedChildren })
          }
        />
        <Stepper
          label="ילדים"
          value={selectedChildren}
          disabled={disabled || !childEligible}
          onChange={(children) =>
            onQuantityChange({ adults: selectedAdults, children })
          }
        />
        <div className="pkg-opt-attr-card__subtotal" aria-live="polite">
          <span className="pkg-opt-attr-card__subtotal-label">סה״כ לשורה</span>
          <span className="pkg-opt-attr-card__subtotal-value">
            {Number.isFinite(subtotalIls)
              ? `${Math.round(subtotalIls).toLocaleString()} ₪`
              : "—"}
          </span>
        </div>
        {lineLocked ? (
          <p className="pkg-opt-attr-card__locked-hint" role="status">
            נוסף להזמנה — הכמויות נקבעות בשרת
          </p>
        ) : null}
      </div>
      ) : null}
    </article>
  );
}
